import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Clock, LogIn, LogOut, Coffee, Loader2, Delete, KeyRound, CheckCircle, Ban, AlertTriangle } from 'lucide-react';

/**
 * Clock In/Out
 *
 * Owner deprecated the dedicated kiosk station login (May 31 2026). Staff
 * now clock themselves in/out from their own logged-in session — selfie
 * + GPS still enforced by the backend. Director keeps the same self-clock
 * UI; manager / chef / bartender / shisha_expert / waiter now share the
 * exact same screen.
 */
export default function ClockPage() {
  const { user } = useAuth();
  const CLOCKABLE_ROLES = ['director', 'manager', 'chef', 'bartender', 'shisha_expert', 'waiter'];
  if (CLOCKABLE_ROLES.includes(user?.role)) return <DirectorSelfClock />;
  return <StaffReadOnly />;
}

// =================================================================
// KIOSK MODE
// =================================================================
function KioskMode() {
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState(null); // staff tile chosen
  const [pin, setPin] = useState('');
  const [action, setAction] = useState(null); // 'in' | 'out' | 'break-start' | 'break-end'
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState(null); // {kind, name, time?, message}
  const [now, setNow] = useState(new Date());
  // Camera state. We keep the stream live the entire time a staff tile is open
  // so the capture-on-submit is instant (no shutter lag).
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState('');

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  // Start / stop the front camera based on whether a staff tile is selected.
  // Break actions don't need a selfie, so we still start the camera (cheap)
  // but the capture call is skipped for break-start / break-end.
  useEffect(() => {
    if (!selected) {
      // Tear down stream when we leave the PIN screen
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      setCameraReady(false);
      setCameraError('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 480 }, height: { ideal: 360 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setCameraReady(true);
      } catch (e) {
        setCameraError(e.name === 'NotAllowedError' ? 'Camera permission denied' : 'Camera unavailable');
        setCameraReady(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected]);

  const captureSelfie = () => {
    if (!cameraReady) return null;
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || !v.videoWidth) return null;
    // Downscale for storage — 320x240 is plenty for audit thumbnail.
    const targetW = 320;
    const ratio = targetW / v.videoWidth;
    c.width = targetW;
    c.height = Math.round(v.videoHeight * ratio);
    const ctx = c.getContext('2d');
    // Mirror so the saved photo matches what staff saw on screen.
    ctx.save();
    ctx.translate(c.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(v, 0, 0, c.width, c.height);
    ctx.restore();
    return c.toDataURL('image/jpeg', 0.6);
  };

  const fetchStaff = useCallback(async () => {
    try {
      const { data } = await api.get('/staff/kiosk/staff');
      setStaff(data);
    } catch { /* the auth gate ensures role=kiosk; ignore transient */ }
  }, []);
  useEffect(() => { fetchStaff(); const t = setInterval(fetchStaff, 30000); return () => clearInterval(t); }, [fetchStaff]);

  const resetFlow = () => { setSelected(null); setPin(''); setAction(null); };

  // After a result confirmation, auto return to staff grid
  useEffect(() => {
    if (!result) return;
    const t = setTimeout(() => { setResult(null); resetFlow(); fetchStaff(); }, 3500);
    return () => clearTimeout(t);
  }, [result, fetchStaff]);

  const submit = async (actionKey) => {
    if (!selected || pin.length !== 4) return;
    // Clock-in / clock-out REQUIRE a selfie. Breaks don't.
    const needsSelfie = actionKey === 'in' || actionKey === 'out';
    let selfie = null;
    if (needsSelfie) {
      if (!cameraReady) {
        toast.error(cameraError || 'Camera is starting — please try again');
        return;
      }
      selfie = captureSelfie();
      if (!selfie) {
        toast.error('Selfie capture failed — please try again');
        return;
      }
    }
    setWorking(true);
    setAction(actionKey);
    const endpoint = {
      'in': '/staff/clock/in',
      'out': '/staff/clock/out',
      'break-start': '/staff/clock/break-start',
      'break-end': '/staff/clock/break-end',
    }[actionKey];
    const payload = { user_email: selected.email, pin };
    if (selfie) payload.selfie_base64 = selfie;
    try {
      const { data } = await api.post(endpoint, payload);
      const label = {
        'in': 'Clocked In',
        'out': 'Clocked Out',
        'break-start': 'Break Started',
        'break-end': 'Break Ended',
      }[actionKey];
      setResult({
        kind: actionKey,
        name: selected.name,
        label,
        clockIn: data.clock_in_time,
        clockOut: data.clock_out_time,
        totalHours: data.total_hours,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
      setPin('');
      setAction(null);
    } finally {
      setWorking(false);
    }
  };

  const pressKey = (k) => {
    if (pin.length >= 4) return;
    setPin(p => p + k);
  };
  const backspace = () => setPin(p => p.slice(0, -1));
  // When PIN reaches 4 digits and action is already chosen, auto-submit.
  // (Common flow: user picks Clock In first, then types PIN.)
  useEffect(() => {
    if (pin.length === 4 && action && !working && selected) {
      submit(action);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  // Action button click: if PIN is already 4 digits, fire submit immediately.
  // Otherwise just remember the intent and submit auto-fires on the 4th digit.
  const pickAction = (k) => {
    if (pin.length === 4) { submit(k); return; }
    setAction(k);
  };

  const timeStr = now.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const dateStr = now.toLocaleDateString('en-AU', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  if (result) {
    const accent = result.kind === 'out' ? 'text-red-400' : result.kind.startsWith('break') ? 'text-amber-400' : 'text-emerald-400';
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center animate-fadeIn" data-testid="kiosk-confirmation">
        <CheckCircle className={`w-24 h-24 ${accent} mb-6`} />
        <h2 className="text-3xl font-serif text-white mb-1">{result.label}</h2>
        <p className="text-zinc-400 text-lg font-sans mb-6">{result.name}</p>
        <div className="bg-white/[0.03] border border-white/10 rounded-sm p-4 min-w-[280px] text-center">
          {result.clockIn && (
            <div className="flex justify-between items-center text-sm font-sans">
              <span className="text-zinc-500">Clock In</span>
              <span className="text-white font-mono">{result.clockIn}</span>
            </div>
          )}
          {result.clockOut && (
            <div className="flex justify-between items-center text-sm font-sans mt-2">
              <span className="text-zinc-500">Clock Out</span>
              <span className="text-white font-mono">{result.clockOut}</span>
            </div>
          )}
          {typeof result.totalHours === 'number' && result.totalHours > 0 && (
            <div className="flex justify-between items-center text-sm font-sans mt-2 pt-2 border-t border-white/5">
              <span className="text-zinc-500">Total</span>
              <span className="text-gold font-mono font-semibold">{result.totalHours}h</span>
            </div>
          )}
        </div>
        <p className="text-zinc-600 text-xs font-sans mt-6">Returning to staff list…</p>
      </div>
    );
  }

  // PIN screen
  if (selected) {
    const presentRole = selected.role[0].toUpperCase() + selected.role.slice(1).replace('_', ' ');
    return (
      <div className="max-w-md mx-auto animate-fadeIn" data-testid="kiosk-pin-screen">
        <button onClick={resetFlow} className="text-zinc-500 hover:text-zinc-300 text-xs font-sans mb-6 flex items-center gap-1" data-testid="kiosk-back">
          ← Back to staff list
        </button>
        <div className="text-center mb-6">
          <div className="relative w-32 h-32 mx-auto mb-3">
            {/* Live camera preview — mirror image so it matches what the person sees. */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-32 h-32 rounded-full object-cover border-2 border-gold/30 bg-black scale-x-[-1]"
              data-testid="kiosk-selfie-video"
            />
            {!cameraReady && (
              <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center text-[10px] font-sans text-zinc-400 text-center px-2" data-testid="kiosk-camera-status">
                {cameraError || 'Starting camera…'}
              </div>
            )}
            {cameraReady && (
              <span className="absolute bottom-1 right-1 w-3 h-3 rounded-full bg-emerald-400 border-2 border-bg-primary animate-pulse" title="Camera live" />
            )}
            <canvas ref={canvasRef} className="hidden" />
          </div>
          <h2 className="text-2xl font-serif text-white">{selected.name}</h2>
          <p className="text-zinc-500 text-xs font-sans uppercase tracking-[0.18em]">{presentRole}</p>
        </div>

        {/* PIN dots */}
        <div className="flex items-center justify-center gap-3 mb-6">
          {[0,1,2,3].map(i => (
            <div key={i} className={`w-4 h-4 rounded-full border-2 ${pin.length > i ? 'bg-gold border-gold' : 'border-white/20'}`} data-testid={`pin-dot-${i}`} />
          ))}
        </div>

        {/* Numeric keypad */}
        <div className="grid grid-cols-3 gap-2 mb-6">
          {[1,2,3,4,5,6,7,8,9].map(n => (
            <button key={n} onClick={() => pressKey(String(n))} disabled={working}
              className="h-14 rounded-sm bg-white/[0.04] border border-white/10 text-white text-xl font-mono hover:bg-white/[0.08] active:scale-95 transition disabled:opacity-50"
              data-testid={`pin-key-${n}`}>
              {n}
            </button>
          ))}
          <button onClick={() => setPin('')} disabled={working}
            className="h-14 rounded-sm bg-transparent border border-white/10 text-zinc-500 text-xs font-sans hover:bg-white/[0.04] active:scale-95 transition disabled:opacity-50"
            data-testid="pin-key-clear">
            CLEAR
          </button>
          <button onClick={() => pressKey('0')} disabled={working}
            className="h-14 rounded-sm bg-white/[0.04] border border-white/10 text-white text-xl font-mono hover:bg-white/[0.08] active:scale-95 transition disabled:opacity-50"
            data-testid="pin-key-0">
            0
          </button>
          <button onClick={backspace} disabled={working}
            className="h-14 rounded-sm bg-transparent border border-white/10 text-zinc-400 flex items-center justify-center hover:bg-white/[0.04] active:scale-95 transition disabled:opacity-50"
            data-testid="pin-key-backspace">
            <Delete className="w-5 h-5" />
          </button>
        </div>

        {/* Action buttons — disabled until PIN has 4 digits */}
        <div className={`grid ${selected.state === 'in' ? 'grid-cols-2' : 'grid-cols-1'} gap-2`}>
          {selected.state === 'out' && (
            <Button onClick={() => pickAction('in')} disabled={working}
              className="h-14 bg-gold text-black font-bold rounded-sm font-sans" data-testid="action-clock-in">
              {working && action === 'in' ? <Loader2 className="w-5 h-5 animate-spin" /> : <><LogIn className="w-5 h-5 mr-2" /> Clock In</>}
            </Button>
          )}
          {selected.state === 'in' && (
            <>
              {!selected.on_break ? (
                <Button onClick={() => pickAction('break-start')} disabled={working}
                  className="h-14 bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-amber-500/25 rounded-sm font-sans font-bold" data-testid="action-break-start">
                  {working && action === 'break-start' ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Coffee className="w-5 h-5 mr-2" /> Break</>}
                </Button>
              ) : (
                <Button onClick={() => pickAction('break-end')} disabled={working}
                  className="h-14 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25 rounded-sm font-sans font-bold animate-pulse" data-testid="action-break-end">
                  {working && action === 'break-end' ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Coffee className="w-5 h-5 mr-2" /> End Break</>}
                </Button>
              )}
              <Button onClick={() => pickAction('out')} disabled={working || selected.on_break}
                className="h-14 bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25 rounded-sm font-sans font-bold" data-testid="action-clock-out">
                {working && action === 'out' ? <Loader2 className="w-5 h-5 animate-spin" /> : <><LogOut className="w-5 h-5 mr-2" /> Clock Out</>}
              </Button>
            </>
          )}
          {selected.state === 'shift_ended' && (
            <div className="text-center text-zinc-500 font-sans text-sm py-4 bg-white/[0.02] rounded-sm border border-white/5" data-testid="shift-ended-banner">
              Shift complete for today. See you tomorrow.
            </div>
          )}
        </div>
      </div>
    );
  }

  // Staff tile grid (default)
  return (
    <div className="animate-fadeIn" data-testid="kiosk-staff-grid">
      <div className="text-center mb-8">
        <p className="text-5xl font-mono text-gold tracking-wider" data-testid="kiosk-clock">{timeStr}</p>
        <p className="text-zinc-500 font-sans text-sm mt-1">{dateStr}</p>
        <p className="text-zinc-600 text-xs uppercase tracking-[0.2em] font-sans mt-4">Tap your name to clock in / out</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {staff.map(s => {
          const stateColor =
            s.state === 'in' ? (s.on_break ? 'border-amber-500/50' : 'border-emerald-500/50')
            : s.state === 'shift_ended' ? 'border-white/10 opacity-60'
            : 'border-white/10';
          const stateLabel =
            s.state === 'in' ? (s.on_break ? 'On Break' : `In · ${s.clock_in_time}`)
            : s.state === 'shift_ended' ? 'Done'
            : 'Tap to clock in';
          return (
            <button
              key={s.id}
              onClick={() => setSelected(s)}
              className={`text-left bg-white/[0.03] border ${stateColor} rounded-sm p-4 hover:bg-white/[0.06] active:scale-[0.98] transition card-hover`}
              data-testid={`kiosk-staff-${s.email}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-sm bg-gold/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-gold font-serif">{s.name[0]?.toUpperCase()}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-white font-sans text-sm truncate font-medium">{s.name}</p>
                  <p className="text-zinc-600 text-[10px] uppercase tracking-wider font-sans">{s.role.replace('_', ' ')}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] font-sans">
                <span className={`w-1.5 h-1.5 rounded-full ${s.state === 'in' ? (s.on_break ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400') : 'bg-zinc-700'}`} />
                <span className="text-zinc-400">{stateLabel}</span>
              </div>
            </button>
          );
        })}
      </div>
      {staff.length === 0 && (
        <p className="text-zinc-600 text-center py-12 font-sans text-sm">Loading staff…</p>
      )}
    </div>
  );
}

// =================================================================
// DIRECTOR SELF CLOCK (exempt from kiosk requirement)
// =================================================================
// =================================================================
// REGENERATE ALL PINS — Director-only nuclear button.
// Owner ask Feb 2026 (after deploying the kiosk to prod and finding
// every staff still shared PIN `0000`): give the director an in-app
// way to seed unique PINs without SSH access. Backend assigns a fresh
// non-weak 4-digit PIN per user, returns the full list ONCE so it can
// be handed out in person. After dismissing the dialog the PINs are
// only retrievable by re-running the regeneration.
// =================================================================
function RegenerateAllPinsCard() {
  const [busy, setBusy] = React.useState(false);
  const [list, setList] = React.useState(null);  // [{email, name, role, pin}, ...]
  // Feb 2026 round 2: separate "Show current PINs" so the director
  // can recover a single forgotten PIN without nuking everyone.
  const [revealList, setRevealList] = React.useState(null);
  const [revealing, setRevealing] = React.useState(false);

  const regen = async () => {
    if (busy) return;
    if (!window.confirm('Regenerate kiosk PINs for ALL staff?\n\nEvery existing PIN will be replaced with a new unique random 4-digit code. Staff will need the new PINs before they can clock in again — make sure you have a way to hand them out.\n\nProceed?')) return;
    setBusy(true);
    try {
      const { data } = await api.post('/staff/kiosk/regenerate-all-pins');
      setList(data?.pins || []);
      toast.success(`Regenerated ${data?.pins?.length || 0} PINs`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not regenerate PINs');
    } finally {
      setBusy(false);
    }
  };

  const reveal = async () => {
    if (revealing) return;
    setRevealing(true);
    try {
      const { data } = await api.get('/staff/kiosk/current-pins');
      setRevealList(data?.pins || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load current PINs');
    } finally {
      setRevealing(false);
    }
  };

  // Feb 2026 owner ask: "When some other staff trying to login - it
  // says face not recognised." Provide a director-only nuclear button
  // to clear EVERY stored reference photo. The very next clock-in for
  // each staff member then re-enrols their fresh selfie. Use this if
  // initial enrollment captured wrong faces during setup-day chaos.
  const [resettingFaces, setResettingFaces] = React.useState(false);
  const resetAllFaces = async () => {
    if (resettingFaces) return;
    if (!window.confirm(
      'Reset face reference for ALL staff?\n\nEvery staff member will need to re-enrol their face on their next kiosk clock-in (just stand in front of the camera, enter PIN, tap action — the system stores the fresh selfie as the new reference).\n\nUse this if initial face enrollment captured the wrong person on multiple accounts.\n\nProceed?'
    )) return;
    setResettingFaces(true);
    try {
      const { data } = await api.post('/staff/kiosk/reset-all-reference-photos');
      toast.success(data?.message || 'Face references cleared');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reset face references');
    } finally {
      setResettingFaces(false);
    }
  };

  return (
    <>
      <div className="bg-amber-500/[0.05] border border-amber-500/20 rounded-sm p-4 mb-4 flex items-center justify-between gap-4 flex-wrap" data-testid="regenerate-pins-card">
        <div>
          <p className="text-amber-200 font-sans text-sm">Kiosk PIN administration</p>
          <p className="text-zinc-400 font-sans text-xs mt-0.5">
            View current PINs to recover a single forgotten one, or re-roll every staff PIN if one&apos;s leaked.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            onClick={reveal}
            disabled={revealing}
            className="bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 rounded-sm font-sans text-xs uppercase tracking-[0.14em]"
            data-testid="show-current-pins-btn"
          >
            <KeyRound className="w-4 h-4 mr-1.5" />
            {revealing ? 'Loading…' : 'Show Current PINs'}
          </Button>
          <Button
            onClick={regen}
            disabled={busy}
            className="bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-200 rounded-sm font-sans text-xs uppercase tracking-[0.14em]"
            data-testid="regenerate-pins-btn"
          >
            <KeyRound className="w-4 h-4 mr-1.5" />
            {busy ? 'Working…' : 'Regenerate All PINs'}
          </Button>
          <Button
            onClick={resetAllFaces}
            disabled={resettingFaces}
            className="bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-200 rounded-sm font-sans text-xs uppercase tracking-[0.14em]"
            data-testid="reset-all-faces-btn"
            title="Clears stored face reference for every staff — next clock-in re-enrols"
          >
            <KeyRound className="w-4 h-4 mr-1.5" />
            {resettingFaces ? 'Working…' : 'Reset All Faces'}
          </Button>
        </div>
      </div>

      {/* Current PINs dialog — read-only recovery view. */}
      <Dialog open={!!revealList} onOpenChange={(o) => { if (!o) setRevealList(null); }}>
        <DialogContent className="bg-bg-secondary border border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-white flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-amber-300" />
              Current Kiosk PINs
            </DialogTitle>
          </DialogHeader>
          <p className="text-[11px] font-sans text-zinc-400 mb-3">
            Read-only view of every staff member&apos;s active PIN. Rows highlighted in amber are still on the default <span className="font-mono text-amber-300">0000</span> placeholder — those staff cannot clock in until you click <span className="text-amber-300">Regenerate All PINs</span>.
          </p>
          <div className="max-h-[60vh] overflow-y-auto border border-white/5 rounded-sm divide-y divide-white/5" data-testid="current-pins-list">
            {(revealList || []).map((row, idx) => {
              const needs = row.needs_regenerate || row.pin === '0000' || !row.pin;
              return (
                <div key={idx} className={`flex items-center justify-between px-3 py-2.5 ${needs ? 'bg-amber-500/[0.06]' : 'hover:bg-white/[0.02]'}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-sans text-white truncate">{row.name || '—'}</p>
                    <p className="text-[10px] font-sans text-zinc-500 truncate">{row.email} · <span className="uppercase tracking-[0.08em]">{row.role}</span></p>
                  </div>
                  <span
                    className={`font-mono text-xl tracking-[0.18em] tabular-nums ml-3 ${needs ? 'text-amber-500/70' : 'text-amber-300'}`}
                    data-testid={`current-pin-${row.email}`}
                  >
                    {row.pin || '—'}
                  </span>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!list} onOpenChange={(o) => { if (!o) setList(null); }}>
        <DialogContent className="bg-bg-secondary border border-amber-500/30 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-white flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-amber-300" />
              New Kiosk PINs — hand these out in person
            </DialogTitle>
          </DialogHeader>
          <p className="text-[11px] font-sans text-zinc-400 mb-3">
            These PINs are shown <span className="text-amber-300">once</span>. Take a photo of this screen / write them down before closing. They are stored hashed-equivalent (compared exact-match) — there is no way to re-display them after this dialog closes.
          </p>
          <div className="max-h-[60vh] overflow-y-auto border border-white/5 rounded-sm divide-y divide-white/5" data-testid="regenerate-pins-list">
            {(list || []).map((row, idx) => (
              <div key={idx} className="flex items-center justify-between px-3 py-2.5 hover:bg-white/[0.02]">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-sans text-white truncate">{row.name || '—'}</p>
                  <p className="text-[10px] font-sans text-zinc-500 truncate">{row.email} · <span className="uppercase tracking-[0.08em]">{row.role}</span></p>
                </div>
                <span className="font-mono text-xl text-amber-300 tracking-[0.18em] tabular-nums ml-3" data-testid={`pin-${row.email}`}>{row.pin}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DirectorSelfClock() {
  const { user } = useAuth();
  const [todayClock, setTodayClock] = useState(null);
  const [history, setHistory] = useState([]);
  const [working, setWorking] = useState(false);
  const [now, setNow] = useState(new Date());
  // Live camera (same flow as KioskMode but always-on)
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState('');

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  const fetchToday = useCallback(async () => {
    try {
      const [todayRes, histRes] = await Promise.all([
        api.get('/staff/clock/today'),
        api.get('/staff/clock/history?days=14'),
      ]);
      setTodayClock(todayRes.data);
      setHistory(histRes.data || []);
    } catch (err) { console.error('[ClockPage.js] error:', err); }
  }, []);
  useEffect(() => { fetchToday(); }, [fetchToday]);

  // Persistent camera stream while on this page
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 480 }, height: { ideal: 360 } }, audio: false,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
        setCameraReady(true);
      } catch (e) {
        setCameraError(e.name === 'NotAllowedError' ? 'Camera permission denied' : 'Camera unavailable');
      }
    })();
    return () => { cancelled = true; if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); };
  }, []);

  const captureSelfie = () => {
    if (!cameraReady) return null;
    const v = videoRef.current; const c = canvasRef.current;
    if (!v || !c || !v.videoWidth) return null;
    const targetW = 320;
    const ratio = targetW / v.videoWidth;
    c.width = targetW; c.height = Math.round(v.videoHeight * ratio);
    const ctx = c.getContext('2d');
    ctx.save(); ctx.translate(c.width, 0); ctx.scale(-1, 1);
    ctx.drawImage(v, 0, 0, c.width, c.height); ctx.restore();
    return c.toDataURL('image/jpeg', 0.6);
  };

  const fire = async (endpoint, label) => {
    if (!cameraReady) { toast.error(cameraError || 'Camera is starting'); return; }
    const selfie = captureSelfie();
    if (!selfie) { toast.error('Selfie capture failed'); return; }
    setWorking(true);
    try {
      await api.post(endpoint, { selfie_base64: selfie });
      toast.success(label);
      fetchToday();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally { setWorking(false); }
  };

  const isOnBreak = todayClock && todayClock.breaks && todayClock.breaks.some(b => !b.end);
  const isClockedIn = todayClock && !todayClock.clock_out;
  const timeStr = now.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

  return (
    <div className="max-w-2xl mx-auto animate-fadeIn" data-testid="director-clock-page">
      <div className="text-center mb-6">
        <p className="text-5xl font-mono text-gold tracking-wider">{timeStr}</p>
        <p className="text-zinc-500 font-sans text-xs mt-1">{now.toLocaleDateString('en-AU', { weekday: 'long', day: '2-digit', month: 'long' })}</p>
      </div>

      {user?.role === 'director' && <RegenerateAllPinsCard />}

      <div className="bg-white/[0.03] border border-white/10 rounded-sm p-6 mb-4 flex flex-col md:flex-row items-center gap-6">
        <div className="relative">
          <video ref={videoRef} autoPlay playsInline muted className="w-40 h-40 rounded-full object-cover border-2 border-gold/30 bg-black scale-x-[-1]" data-testid="director-selfie-video" />
          {!cameraReady && (
            <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center text-[10px] font-sans text-zinc-400 text-center px-2">
              {cameraError || 'Starting camera…'}
            </div>
          )}
          {cameraReady && <span className="absolute bottom-2 right-2 w-3 h-3 rounded-full bg-emerald-400 border-2 border-bg-primary animate-pulse" />}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        <div className="flex-1 w-full">
          <h2 className="text-xl font-serif text-white mb-1">{user?.name || 'Director'}</h2>
          <p className="text-zinc-500 font-sans text-xs uppercase tracking-[0.18em] mb-4">Director · self clock</p>
          {!isClockedIn ? (
            <Button onClick={() => fire('/staff/clock/in', 'Clocked In')} disabled={working || !cameraReady}
              className="w-full h-12 bg-gold text-black font-bold rounded-sm font-sans" data-testid="director-clock-in">
              {working ? <Loader2 className="w-5 h-5 animate-spin" /> : <><LogIn className="w-5 h-5 mr-2" /> Clock In</>}
            </Button>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {!isOnBreak ? (
                <Button onClick={() => fire('/staff/clock/break-start', 'Break Started')} disabled={working}
                  className="h-12 bg-amber-500/15 text-amber-400 border border-amber-500/30 rounded-sm font-sans font-bold" data-testid="director-break-start">
                  <Coffee className="w-4 h-4 mr-2" /> Break
                </Button>
              ) : (
                <Button onClick={() => fire('/staff/clock/break-end', 'Break Ended')} disabled={working}
                  className="h-12 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded-sm font-sans font-bold animate-pulse" data-testid="director-break-end">
                  <Coffee className="w-4 h-4 mr-2" /> End Break
                </Button>
              )}
              <Button onClick={() => fire('/staff/clock/out', 'Clocked Out')} disabled={working || isOnBreak}
                className="h-12 bg-red-500/15 text-red-400 border border-red-500/30 rounded-sm font-sans font-bold" data-testid="director-clock-out">
                <LogOut className="w-4 h-4 mr-2" /> Clock Out
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Today */}
      <div className="bg-white/[0.03] border border-white/5 rounded-sm p-5 mb-4">
        <h3 className="text-xs font-sans uppercase tracking-[0.18em] text-zinc-500 mb-3">Today</h3>
        {!todayClock ? (
          <p className="text-zinc-600 font-sans text-sm">Not clocked in yet today.</p>
        ) : todayClock.clock_out ? (
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Clock In" value={todayClock.clock_in_time || '-'} />
            <Stat label="Clock Out" value={todayClock.clock_out_time || '-'} />
            <Stat label="Total" value={`${todayClock.total_hours || 0}h`} accent="gold" />
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-400 font-sans text-sm flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Clocked In{isOnBreak ? ' · On Break' : ''}</p>
              <p className="text-white font-sans text-2xl mt-1">{todayClock.clock_in_time}</p>
            </div>
          </div>
        )}
      </div>

      {/* History */}
      <div className="bg-white/[0.03] border border-white/5 rounded-sm p-4">
        <h3 className="text-sm font-sans font-medium text-zinc-400 mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Recent Attendance</h3>
        {history.length === 0 ? (
          <p className="text-zinc-600 font-sans text-sm text-center py-4">No records yet</p>
        ) : (
          <div className="space-y-2">
            {history.slice(0, 14).map(r => (
              <div key={r.id} className={`flex items-center justify-between p-2 bg-white/[0.02] rounded-sm text-sm font-sans ${r.exception ? 'border-l-2 border-amber-500' : ''}`}>
                <div className="flex items-center gap-3">
                  <span className="text-zinc-500 w-20">{r.date}</span>
                  <span className="text-white">{r.clock_in_time || '-'}</span>
                  <span className="text-zinc-600">-</span>
                  <span className="text-white">{r.clock_out_time || '-'}</span>
                </div>
                {r.total_hours > 0 && <span className="text-gold">{r.total_hours}h</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// =================================================================
// STAFF READ-ONLY VIEW (everyone except kiosk)
// =================================================================
function StaffReadOnly() {
  const [todayClock, setTodayClock] = useState(null);
  const [history, setHistory] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [todayRes, histRes] = await Promise.all([
        api.get('/staff/clock/today'),
        api.get('/staff/clock/history?days=14'),
      ]);
      setTodayClock(todayRes.data);
      setHistory(histRes.data || []);
    } catch (err) { console.error('[ClockPage.js] error:', err); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="animate-fadeIn max-w-3xl mx-auto">
      {/* Friendly banner — explains why clock buttons are absent */}
      <div className="bg-amber-500/[0.08] border border-amber-500/30 rounded-sm p-5 mb-6 flex items-start gap-3" data-testid="use-kiosk-banner">
        <Ban className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="text-amber-300 font-sans text-sm font-semibold mb-1">Clock in &amp; out at the lounge kiosk</p>
          <p className="text-zinc-400 text-sm font-sans leading-relaxed">
            Personal phones and laptops can&apos;t be used to clock in or out. Walk over to the kiosk tablet in the lounge, tap your name, enter your 4-digit PIN, and pick an action. Need a PIN reset? Ask a manager.
          </p>
        </div>
      </div>

      {/* Today summary (read-only) */}
      <div className="bg-white/[0.03] border border-white/5 rounded-sm p-5 mb-4">
        <h3 className="text-xs font-sans uppercase tracking-[0.18em] text-zinc-500 mb-3">Today</h3>
        {!todayClock ? (
          <p className="text-zinc-600 font-sans text-sm">You haven&apos;t clocked in yet today.</p>
        ) : todayClock.clock_out ? (
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Clock In" value={todayClock.clock_in_time || '-'} />
            <Stat label="Clock Out" value={todayClock.clock_out_time || '-'} />
            <Stat label="Total" value={`${todayClock.total_hours || 0}h`} accent="gold" />
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-400 font-sans text-sm flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Clocked In</p>
              <p className="text-white font-sans text-2xl mt-1">{todayClock.clock_in_time}</p>
            </div>
            {todayClock.is_late && <Badge className="bg-amber-500/20 text-amber-400 border-0 font-sans text-xs">{todayClock.late_minutes}min late</Badge>}
          </div>
        )}
      </div>

      {/* History */}
      <div className="bg-white/[0.03] border border-white/5 rounded-sm p-4">
        <h3 className="text-sm font-sans font-medium text-zinc-400 mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Recent Attendance</h3>
        {history.length === 0 ? (
          <p className="text-zinc-600 font-sans text-sm text-center py-4">No attendance records yet</p>
        ) : (
          <div className="space-y-2">
            {history.slice(0, 14).map(r => (
              <div key={r.id} className={`flex items-center justify-between p-2 bg-white/[0.02] rounded-sm text-sm font-sans ${r.exception ? 'border-l-2 border-amber-500' : ''}`} data-testid={`history-${r.id}`}>
                <div className="flex items-center gap-3">
                  <span className="text-zinc-500 w-20">{r.date}</span>
                  <span className="text-white">{r.clock_in_time || '-'}</span>
                  <span className="text-zinc-600">-</span>
                  <span className="text-white">{r.clock_out_time || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  {r.total_hours > 0 && <span className="text-gold">{r.total_hours}h</span>}
                  {r.is_late && <Badge className="bg-amber-500/20 text-amber-400 border-0 text-[10px]">Late</Badge>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className="bg-white/[0.02] rounded-sm p-3 text-center">
    <p className={`font-sans text-lg font-semibold ${accent === 'gold' ? 'text-gold' : 'text-white'}`}>{value}</p>
    <p className="text-zinc-500 font-sans text-[10px] uppercase">{label}</p>
  </div>
);
